import { useState, useRef } from 'react';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { Upload, Download, FileSpreadsheet, Loader2, AlertCircle } from 'lucide-react';

export default function UploadSection() {
  const { uploadExcel, uploads, getFileDownloadURL } = useData();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setUploadResult({ error: 'Seleziona un file .xlsx' });
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    try {
      const result = await uploadExcel(file, setUploadProgress);
      setUploadResult(result);
    } catch (err) {
      setUploadResult({ error: err.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDownloadFile = async (storagePath) => {
    try {
      const url = await getFileDownloadURL(storagePath);
      if (url) window.open(url, '_blank');
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" data-testid="open-upload-modal">
          <Upload className="w-4 h-4 mr-2" />
          Carica File Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Gestione File Excel</DialogTitle>
          <DialogDescription>Carica un nuovo file o consulta la cronologia degli upload</DialogDescription>
        </DialogHeader>

        <div className="space-y-5" data-testid="upload-section">
          <div className="border-2 border-dashed border-fw-border rounded-lg p-6 text-center hover:border-fw-yellow transition-colors duration-200">
            <FileSpreadsheet className="w-10 h-10 text-fw-subtle mx-auto mb-3" />
            <p className="text-sm text-fw-muted mb-3">Seleziona un file .xlsx</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              id="excel-upload"
              data-testid="file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="border-fw-yellow/30 text-fw-dark hover:bg-yellow-50"
              data-testid="upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Caricamento...' : 'Scegli File'}
            </Button>
          </div>

          {uploading && (
            <div data-testid="upload-progress">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-fw-muted mt-1">{uploadProgress}% completato</p>
            </div>
          )}

          {uploadResult && !uploadResult.error && (
            <div className="p-3 bg-green-50 border border-fw-green/20 rounded-md" data-testid="upload-success">
              <p className="text-sm font-medium text-green-700">Importazione completata</p>
              <p className="text-sm text-green-700">Importate: {uploadResult.imported} | Scartate: {uploadResult.skipped}</p>
              {uploadResult.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-green-600 cursor-pointer">Vedi dettagli errori ({uploadResult.errors.length})</summary>
                  <ul className="mt-1 space-y-0.5">
                    {uploadResult.errors.slice(0, 10).map((e, i) => (
                      <li key={i} className="text-xs text-green-600">Riga {e.row}: {e.message}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {uploadResult?.error && (
            <div className="p-3 bg-red-50 border border-fw-red/20 rounded-md" data-testid="upload-error">
              <p className="text-sm text-fw-red flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {uploadResult.error}
              </p>
            </div>
          )}

          {uploads.length > 0 && (
            <div data-testid="uploads-history">
              <h3 className="text-sm font-semibold text-fw-dark mb-2">Cronologia Upload</h3>
              <div className="divide-y divide-fw-border/50 border border-fw-border rounded-md max-h-48 overflow-y-auto">
                {uploads.map((u) => (
                  <div key={u._docId} className="flex items-center justify-between px-4 py-2.5 hover:bg-fw-light transition-colors duration-150">
                    <div>
                      <p className="text-sm font-medium text-fw-dark">{u.fileName}</p>
                      <p className="text-xs text-fw-subtle">
                        {u.importedRows} importate, {u.skippedRows} scartate
                      </p>
                    </div>
                    {u.storagePath && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadFile(u.storagePath)}
                        className="text-fw-blue hover:text-fw-blue hover:bg-blue-50"
                        data-testid={`download-file-${u._docId}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
